import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  ChevronRight,
  CheckCircle2,
  Copy,
  Plus,
  Sparkles,
  UserRound,
  X,
} from "lucide-react";
import { AdminDrawer } from "@/components/admin/AdminDrawer";
import {
  FloatingToast,
  type FloatingToastState,
} from "@/components/feedback/FloatingToast";
import { InlineLoadError } from "@/components/feedback/InlineLoadError";
import { QuotationAssignmentDialog } from "@/components/quotation/QuotationAssignmentDialog";
import { QuotationCopyOverwriteModal } from "@/components/quotation/QuotationCopyOverwriteModal";
import { QuotationDeactivateConfirmation } from "@/components/quotation/QuotationDeactivateConfirmation";
import { QuotationSpecialsDialog } from "@/components/quotation/QuotationSpecialsDialog";
import { QuickDateChooser } from "@/components/quotation/QuickDateChooser";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import { ApiError } from "@/api/client";
import {
  QUOTATION_HISTORY_PAGE_SIZE,
  useActivateQuotationForAllClients,
  useBootstrapQuotation,
  useCopyInto,
  useCreateQuotation,
  useDeactivateAllQuotationPublications,
  useDeactivateQuotationForAllClients,
  useFindQuotationByDate,
  useQuotations,
} from "@/features/quotation/hooks";
import type {
  DailyQuotation,
  QuotationHistoryItem,
  QuotationStatus,
} from "@/features/quotation/types";
import {
  getCuritibaDate,
  isQuotationDatePast,
} from "@/features/quotation/publication";
import {
  displayToIsoDate,
  formatQuotationHistoryDate,
  isoToDisplayDate,
} from "@/features/quotation/dateInput";
import { formatDateBR } from "@/lib/timeAgo";
import "./QuotationWorkspace.css";
import "@/components/quotation/quotation-mock.css";
import "./quotation/QuotationListCohesion.css";

const STATUS_LABELS: Record<QuotationStatus, string> = {
  not_started: "Não iniciada",
  in_progress: "Aberta",
  completed: "Concluída",
};

type Toast = {
  title: string;
  message: string;
  tone: "success" | "error";
  state: FloatingToastState;
};

export function QuotationHistoryPage({ canDelete }: { canDelete: boolean }) {
  const navigate = useNavigate();
  const [offset, setOffset] = useState(0);
  const quotationsQuery = useQuotations({
    limit: QUOTATION_HISTORY_PAGE_SIZE,
    offset,
  });
  const createMutation = useCreateQuotation();
  const findQuotationMutation = useFindQuotationByDate();
  const bootstrapMutation = useBootstrapQuotation();
  const copyIntoMutation = useCopyInto();
  const activateMutation = useActivateQuotationForAllClients();
  const deactivateMutation = useDeactivateQuotationForAllClients();
  const deactivateAllMutation = useDeactivateAllQuotationPublications();
  const [assignmentQuotation, setAssignmentQuotation] =
    useState<QuotationHistoryItem | null>(null);
  const [assignmentPurpose, setAssignmentPurpose] = useState<
    "manage" | "complete"
  >("manage");
  const [specialsQuotation, setSpecialsQuotation] =
    useState<QuotationHistoryItem | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newDateText, setNewDateText] = useState("");
  const [copySource, setCopySource] = useState<QuotationHistoryItem | null>(
    null,
  );
  const [copyDateText, setCopyDateText] = useState("");
  const [copyTarget, setCopyTarget] = useState<DailyQuotation | null>(null);
  const [copyModalReady, setCopyModalReady] = useState(false);
  const [restoreCopyConfirmFocus, setRestoreCopyConfirmFocus] = useState(false);
  const copyConfirmRef = useRef<HTMLButtonElement | null>(null);
  const copyOperation = useRef(0);
  const publicationInFlight = useRef(false);
  const [confirmGlobalDeactivate, setConfirmGlobalDeactivate] = useState(false);
  const [confirmRowDeactivate, setConfirmRowDeactivate] =
    useState<QuotationHistoryItem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const data = quotationsQuery.data;
  const rows = data?.items ?? [];
  const limit = Math.max(1, data?.limit ?? QUOTATION_HISTORY_PAGE_SIZE);
  const total = data?.total ?? 0;
  const returnedOffset = data?.offset ?? offset;
  const today = getCuritibaDate();

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(
      () => {
        if (toast.state === "exiting") setToast(null);
        else
          setToast((current) =>
            current ? { ...current, state: "exiting" } : null,
          );
      },
      toast.state === "exiting" ? 180 : 4200,
    );
    return () => window.clearTimeout(timeout);
  }, [toast]);

  useEffect(() => {
    if (!data) return;
    const lastOffset = total ? Math.floor((total - 1) / limit) * limit : 0;
    if (offset > lastOffset) setOffset(lastOffset);
  }, [data, limit, offset, total]);

  function notify(
    title: string,
    message: string,
    tone: Toast["tone"] = "success",
  ) {
    setToast({ title, message, tone, state: "visible" });
  }

  async function runPublicationAction(
    action: () => Promise<unknown>,
    success: string,
  ) {
    if (publicationInFlight.current) return;
    publicationInFlight.current = true;
    setError(null);
    try {
      await action();
      notify("Publicação atualizada", success);
    } catch (cause) {
      notify(
        "Não foi possível atualizar",
        getUserFacingErrorMessage(
          cause,
          "Não foi possível atualizar a publicação.",
        ),
        "error",
      );
    } finally {
      publicationInFlight.current = false;
    }
  }

  async function createQuotation() {
    if (createMutation.isPending) return;
    const date = displayToIsoDate(newDateText);
    if (!date) return;
    setError(null);
    try {
      await createMutation.mutateAsync({ date });
      setShowCreate(false);
      setNewDateText("");
    } catch (cause) {
      if (
        cause instanceof ApiError &&
        cause.status === 409 &&
        cause.message.toLowerCase().includes("quotation")
      ) {
        setShowCreate(false);
        setNewDateText("");
        notify(
          "Data indisponível",
          "Já existe uma cotação para essa data.",
          "error",
        );
        return;
      }
      setError(
        getUserFacingErrorMessage(cause, "Não foi possível criar a cotação."),
      );
    }
  }

  async function chooseCopyTarget() {
    if (!copySource) return;
    const date = displayToIsoDate(copyDateText);
    if (!date || findQuotationMutation.isPending || bootstrapMutation.isPending)
      return;
    setError(null);
    const operation = ++copyOperation.current;
    try {
      const existing = await findQuotationMutation.mutateAsync({
        date,
        excludeId: copySource.id,
      });
      if (operation !== copyOperation.current) return;
      if (existing) {
        setCopyModalReady(false);
        setCopyTarget(existing);
        return;
      }
      await bootstrapMutation.mutateAsync({
        sourceId: copySource.id,
        payload: { targetDate: date },
      });
      if (operation !== copyOperation.current) return;
      setCopySource(null);
      setCopyDateText("");
      notify("Copiado", "Cotação copiada com sucesso.");
    } catch (cause) {
      if (operation !== copyOperation.current) return;
      setError(
        getUserFacingErrorMessage(
          cause,
          "Não foi possível verificar a data selecionada.",
        ),
      );
    }
  }

  function closeCopyChooser() {
    copyOperation.current += 1;
    setCopyModalReady(false);
    setCopyTarget(null);
    setCopySource(null);
    setCopyDateText("");
    setError(null);
  }

  async function overwriteCopy() {
    if (!copySource || !copyTarget) return;
    setError(null);
    try {
      await copyIntoMutation.mutateAsync({
        sourceId: copySource.id,
        targetId: copyTarget.id,
      });
      setCopyTarget(null);
      setCopySource(null);
      setCopyDateText("");
      notify("Copiado", "Cotação copiada com sucesso.");
    } catch (cause) {
      setError(
        getUserFacingErrorMessage(cause, "Não foi possível copiar a cotação."),
      );
    }
  }

  const pending =
    activateMutation.isPending ||
    deactivateMutation.isPending ||
    deactivateAllMutation.isPending;
  const page = Math.floor(returnedOffset / limit) + 1;
  const pageCount = Math.max(1, Math.ceil(total / limit));
  const visiblePages = [...new Set([1, page - 1, page, page + 1, pageCount])]
    .filter((value) => value >= 1 && value <= pageCount)
    .sort((a, b) => a - b);

  return (
    <main className="quotation-history-page lnk-page lnk-super-page app-page-frame">
      <header className="lnk-page-header lnk-super-header lnk-history-header">
        <div>
          <h1>Cotações</h1>
        </div>
        <div className="lnk-super-actions">
          <button
            type="button"
            className="lnk-deactivate-everything"
            disabled={pending}
            onClick={() => {
              if (!publicationInFlight.current && !pending)
                setConfirmGlobalDeactivate(true);
            }}
          >
            <X size={17} /> Desativar todas
          </button>
          <button
            type="button"
            className="primary lnk-new-quotation"
            onClick={() => setShowCreate(true)}
          >
            <Plus size={17} /> Nova cotação
          </button>
        </div>
      </header>
      <div className="lnk-history-table">
        <div
          className="lnk-history-rows"
          role="table"
          aria-label="Histórico de cotações"
        >
          <div className="lnk-history-head" role="row">
            <span role="columnheader">Data</span>
            <span role="columnheader">Status</span>
            <span role="columnheader">Clientes ativos</span>
            <span role="columnheader">Especiais</span>
            <span role="columnheader">Ações</span>
          </div>
          {rows.map((row) => {
            const past = isQuotationDatePast(row.date, today);
            return (
              <article
                role="row"
                className={
                  !past && row.active_client_count > 0 ? "is-active" : ""
                }
                key={row.id}
                onClick={(event) => {
                  if (
                    (event.target as Element).closest(
                      "button, a, input, select, textarea",
                    )
                  )
                    return;
                  navigate({ to: `/cotacoes/${row.id}` as string });
                }}
              >
                <div role="cell">
                  <button
                    type="button"
                    className="lnk-history-open"
                    aria-label={formatDateBR(row.date)}
                    onClick={() =>
                      navigate({ to: `/cotacoes/${row.id}` as string })
                    }
                  >
                    <strong>{formatQuotationHistoryDate(row.date)}</strong>
                  </button>
                </div>
                <span role="cell" className="lnk-history-status">
                  {STATUS_LABELS[row.status]}
                </span>
                <div role="cell">
                  <button
                    type="button"
                    className={`lnk-history-count ${past ? "is-unavailable" : ""}`}
                    aria-label={`Clientes ativos: ${row.active_client_count}`}
                    disabled={past}
                    onClick={() => {
                      setAssignmentPurpose("manage");
                      setAssignmentQuotation(row);
                    }}
                  >
                    <UserRound size={15} />
                    <strong>{row.active_client_count}</strong>
                    <span className="lnk-history-count__full">
                      {past ? "clientes · encerrada" : "clientes · gerenciar"}
                    </span>
                    <span className="lnk-history-count__mobile">clientes</span>
                    {past ? null : <ChevronRight size={15} />}
                  </button>
                </div>
                <div role="cell">
                  <button
                    type="button"
                    className="lnk-history-count"
                    aria-label={`Cotações especiais: ${row.special_count}`}
                    onClick={() => setSpecialsQuotation(row)}
                  >
                    <Sparkles size={15} />
                    <strong>{row.special_count}</strong>
                    <span className="lnk-history-count__full">
                      especiais · abrir
                    </span>
                    <span className="lnk-history-count__mobile">especiais</span>
                    <ChevronRight size={15} />
                  </button>
                </div>
                <div role="cell" className="lnk-history-actions">
                  {!past && row.status === "in_progress" ? (
                    <button
                      type="button"
                      className="conclude-action"
                      onClick={() => {
                        setAssignmentPurpose("complete");
                        setAssignmentQuotation(row);
                      }}
                    >
                      <CheckCircle2 size={15} /> Concluir
                    </button>
                  ) : null}
                  {!past &&
                  (row.active_client_count > 0 ||
                    row.status === "completed") ? (
                    row.active_client_count > 0 ? (
                      <button
                        type="button"
                        className="deactivate-all"
                        aria-label="Desativar para todos"
                        onClick={() => {
                          if (!publicationInFlight.current && !pending)
                            setConfirmRowDeactivate(row);
                        }}
                        disabled={pending}
                      >
                        <X size={15} /> Desativar para todos
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="activate-all"
                        aria-label="Ativar para todos"
                        onClick={() =>
                          void runPublicationAction(
                            () => activateMutation.mutateAsync(row.id),
                            "Cotação ativada para todos.",
                          )
                        }
                        disabled={pending}
                      >
                        <CheckCircle2 size={15} /> Ativar para todos
                      </button>
                    )
                  ) : null}
                  <button
                    type="button"
                    className="copy-action"
                    onClick={() => {
                      copyOperation.current += 1;
                      setRestoreCopyConfirmFocus(false);
                      setCopyModalReady(false);
                      setCopyTarget(null);
                      setCopySource(row);
                      setCopyDateText("");
                    }}
                  >
                    <Copy size={15} /> Copiar
                  </button>
                </div>
              </article>
            );
          })}
        </div>
        <nav
          className="lnk-history-pagination"
          aria-label="Paginação de cotações"
          aria-busy={quotationsQuery.isFetching || undefined}
        >
          <span className="lnk-history-range">
            {total ? returnedOffset + 1 : 0}–
            {Math.min(returnedOffset + limit, total)} de {total}
          </span>
          <div>
            <button
              type="button"
              disabled={quotationsQuery.isFetching || page === 1}
              onClick={() => setOffset((page - 2) * limit)}
            >
              Anterior
            </button>
            {visiblePages.map((pageNumber, index) => (
              <span key={pageNumber} className="lnk-page-number-wrap">
                {index > 0 && pageNumber - visiblePages[index - 1]! > 1 ? (
                  <i aria-hidden="true">…</i>
                ) : null}
                <button
                  type="button"
                  className={page === pageNumber ? "current" : ""}
                  aria-label={`Página ${pageNumber}`}
                  aria-current={page === pageNumber ? "page" : undefined}
                  disabled={quotationsQuery.isFetching}
                  onClick={() => setOffset((pageNumber - 1) * limit)}
                >
                  {pageNumber}
                </button>
              </span>
            ))}
            <button
              type="button"
              disabled={quotationsQuery.isFetching || page === pageCount}
              onClick={() => setOffset(page * limit)}
            >
              Próxima
            </button>
          </div>
        </nav>
      </div>
      {toast ? <FloatingToast {...toast} /> : null}
      {quotationsQuery.isError && !data ? (
        <InlineLoadError
          title="Cotações indisponíveis"
          message="Não foi possível carregar as cotações."
          onRetry={() => void quotationsQuery.refetch()}
          isRetrying={quotationsQuery.isFetching}
        />
      ) : null}
      {error ? (
        <p className="quotation-dialog-error" role="alert">
          {error}
        </p>
      ) : null}

      <QuotationAssignmentDialog
        quotation={assignmentQuotation}
        purpose={assignmentPurpose}
        onClose={() => setAssignmentQuotation(null)}
      />
      {specialsQuotation ? (
        <QuotationSpecialsDialog
          quotation={specialsQuotation}
          canDelete={canDelete}
          onClose={() => setSpecialsQuotation(null)}
        />
      ) : null}
      <AdminDrawer
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onSubmit={createQuotation}
        submitDisabled={createMutation.isPending}
        title="Nova cotação"
      >
        <QuickDateChooser
          customDateText={newDateText}
          confirmLabel="Criar"
          isPending={createMutation.isPending}
          onCustomDateChange={setNewDateText}
          onPickToday={() => setNewDateText(isoToDisplayDate(today))}
          onPickTomorrow={() =>
            setNewDateText(
              isoToDisplayDate(
                getCuritibaDate(new Date(Date.now() + 24 * 60 * 60 * 1000)),
              ),
            )
          }
          onConfirm={createQuotation}
        />
      </AdminDrawer>
      <AdminDrawer
        open={Boolean(copySource) && !copyTarget}
        onClose={closeCopyChooser}
        onSubmit={chooseCopyTarget}
        submitDisabled={
          bootstrapMutation.isPending || findQuotationMutation.isPending
        }
        onAfterClose={() => {
          if (copyTarget) setCopyModalReady(true);
        }}
        initialFocusRef={restoreCopyConfirmFocus ? copyConfirmRef : undefined}
        title="Copiar cotação"
      >
        <QuickDateChooser
          customDateText={copyDateText}
          confirmLabel="Copiar"
          isPending={
            bootstrapMutation.isPending || findQuotationMutation.isPending
          }
          onCustomDateChange={setCopyDateText}
          onPickToday={() => setCopyDateText(isoToDisplayDate(today))}
          onPickTomorrow={() =>
            setCopyDateText(
              isoToDisplayDate(
                getCuritibaDate(new Date(Date.now() + 24 * 60 * 60 * 1000)),
              ),
            )
          }
          onConfirm={chooseCopyTarget}
          onCancel={closeCopyChooser}
          confirmRef={copyConfirmRef}
        />
      </AdminDrawer>
      <QuotationCopyOverwriteModal
        target={copySource && copyModalReady ? copyTarget : null}
        copyError={error}
        isPending={copyIntoMutation.isPending}
        onCancel={() => {
          setRestoreCopyConfirmFocus(true);
          setCopyModalReady(false);
          setCopyTarget(null);
        }}
        onConfirm={overwriteCopy}
        returnFocusRef={copyConfirmRef}
      />
      <QuotationDeactivateConfirmation
        open={Boolean(confirmRowDeactivate)}
        title="Desativar cotação?"
        description={`Os clientes deixarão de ver a cotação de ${confirmRowDeactivate ? formatDateBR(confirmRowDeactivate.date) : ""}.`}
        pending={deactivateMutation.isPending}
        onClose={() => setConfirmRowDeactivate(null)}
        onConfirm={() => {
          if (!confirmRowDeactivate) return;
          void runPublicationAction(async () => {
            await deactivateMutation.mutateAsync(confirmRowDeactivate.id);
            setConfirmRowDeactivate(null);
          }, "Cotação desativada para todos.");
        }}
      />
      <QuotationDeactivateConfirmation
        open={confirmGlobalDeactivate}
        title="Desativar todas?"
        description="Nenhum cliente verá uma cotação ativa até uma nova ativação ser feita."
        pending={deactivateAllMutation.isPending}
        onClose={() => setConfirmGlobalDeactivate(false)}
        onConfirm={() =>
          void runPublicationAction(async () => {
            await deactivateAllMutation.mutateAsync();
            setConfirmGlobalDeactivate(false);
          }, "Todas as cotações foram desativadas.")
        }
      />
    </main>
  );
}
